import api from "../../../conf/api";

/**
 * Chunked File Uploader
 * Handles large file uploads by splitting into chunks with parallel upload support
 */

// 5MB chunks — reduces Cloudflare round trips vs the old 2MB
const CHUNK_SIZE = 1024 * 1024 * 5;

// How many chunks to keep in-flight simultaneously
const DEFAULT_CONCURRENCY = 5;

// Per-chunk retry settings
const CHUNK_MAX_RETRIES = 3;
const CHUNK_RETRY_BASE_DELAY_MS = 1000; // 1s, 2s, 4s (exponential)

class ChunkUploader {
  constructor(file, fileType = 'video') {
    this.file = file;
    this.fileType = fileType;
    this.chunkSize = CHUNK_SIZE;
    this.totalChunks = Math.ceil(file.size / this.chunkSize);
    this.uploadId = null;
    this.uploadedChunks = 0;
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
    this.aborted = false;
  }

  /**
   * Initialize upload session
   */
  async initialize() {
    try {
      console.log(`Initializing upload: ${this.file.name} (${(this.file.size / (1024 * 1024)).toFixed(2)} MB, ${this.totalChunks} chunks of ${CHUNK_SIZE / (1024 * 1024)}MB)`);

      const response = await api.post('/mrss/chunk-upload/init/', {
        filename: this.file.name,
        filesize: this.file.size,
        total_chunks: this.totalChunks,
        file_type: this.fileType,
      });

      this.uploadId = response.data.upload_id;
      console.log(`Upload initialized with ID: ${this.uploadId}`);
      return this.uploadId;
    } catch (error) {
      console.error('Failed to initialize upload:', error);
      throw error;
    }
  }

  /**
   * Upload a single chunk (no retry — use _uploadChunkWithRetry for resilience)
   */
  async uploadChunk(chunkNumber) {
    if (this.aborted) {
      throw new Error('Upload aborted');
    }

    const start = chunkNumber * this.chunkSize;
    const end = Math.min(start + this.chunkSize, this.file.size);
    const chunk = this.file.slice(start, end);

    const formData = new FormData();
    formData.append('upload_id', this.uploadId);
    formData.append('chunk_number', chunkNumber);
    formData.append('chunk', chunk, `chunk_${chunkNumber}`);

    const response = await api.post('/mrss/chunk-upload/chunk/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 min per chunk (large chunks + Cloudflare latency)
    });

    this.uploadedChunks++;

    console.log(`Chunk ${chunkNumber + 1}/${this.totalChunks} uploaded (${this.uploadedChunks} done)`);

    if (this.onProgress) {
      this.onProgress({
        uploadedChunks: this.uploadedChunks,
        totalChunks: this.totalChunks,
        progress: (this.uploadedChunks / this.totalChunks) * 100,
        chunkNumber,
      });
    }

    return response.data;
  }

  /**
   * Upload a chunk with automatic retry and exponential backoff
   */
  async _uploadChunkWithRetry(chunkNumber) {
    let lastError;

    for (let attempt = 0; attempt < CHUNK_MAX_RETRIES; attempt++) {
      try {
        return await this.uploadChunk(chunkNumber);
      } catch (error) {
        if (this.aborted) throw error;
        lastError = error;
        if (attempt < CHUNK_MAX_RETRIES - 1) {
          const delay = CHUNK_RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
          console.warn(`Chunk ${chunkNumber} failed (attempt ${attempt + 1}/${CHUNK_MAX_RETRIES}), retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }

    console.error(`Chunk ${chunkNumber} failed after ${CHUNK_MAX_RETRIES} attempts`);
    throw lastError;
  }

  /**
   * Finalize upload by combining chunks on the server
   */
  async finalize() {
    try {
      console.log(`Finalizing upload ${this.uploadId} (${this.uploadedChunks}/${this.totalChunks} chunks)`);

      const response = await api.post('/mrss/chunk-upload/finalize/', {
        upload_id: this.uploadId,
      }, { timeout: 60 * 60 * 1000 });

      return response.data;
    } catch (error) {
      console.error('Failed to finalize upload:', error);
      if (error.response?.data) {
        console.error('Error details:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Upload file sequentially (fallback / simple mode)
   */
  async upload() {
    try {
      await this.initialize();

      for (let i = 0; i < this.totalChunks; i++) {
        if (this.aborted) throw new Error('Upload aborted by user');
        await this._uploadChunkWithRetry(i);
      }

      const result = await this.finalize();

      if (this.onComplete) this.onComplete(result);
      return result;
    } catch (error) {
      if (this.onError) this.onError(error);
      throw error;
    }
  }

  /**
   * Upload chunks in parallel using a sliding window (worker pool).
   *
   * Unlike the old batch approach (wait for 3 → start 3 → repeat),
   * this always keeps `concurrency` chunks in-flight simultaneously.
   * As soon as one finishes, the next one starts — no stalling on slow chunks.
   *
   * This is especially effective against Cloudflare per-request latency:
   * while Cloudflare is verifying chunk N, chunks N+1..N+4 are already being sent.
   */
  async uploadParallel(concurrency = DEFAULT_CONCURRENCY) {
    await this.initialize();

    // Build a queue of chunk indices to process
    const queue = Array.from({ length: this.totalChunks }, (_, i) => i);

    return new Promise((resolve, reject) => {
      let activeWorkers = 0;
      let failed = false;

      const runNext = () => {
        // Spin up workers until we hit the concurrency cap or exhaust the queue
        while (activeWorkers < concurrency && queue.length > 0 && !this.aborted && !failed) {
          const chunkNumber = queue.shift();
          activeWorkers++;

          this._uploadChunkWithRetry(chunkNumber)
            .then(() => {
              activeWorkers--;
              if (failed) return;

              if (queue.length === 0 && activeWorkers === 0) {
                // All chunks uploaded — finalize
                this.finalize()
                  .then((result) => {
                    if (this.onComplete) this.onComplete(result);
                    resolve(result);
                  })
                  .catch((err) => {
                    if (this.onError) this.onError(err);
                    reject(err);
                  });
              } else {
                // A slot opened up — start the next chunk immediately
                runNext();
              }
            })
            .catch((err) => {
              if (failed) return; // Already rejected
              failed = true;
              if (this.onError) this.onError(err);
              reject(err);
            });
        }

        if (this.aborted && !failed) {
          failed = true;
          const err = new Error('Upload aborted by user');
          if (this.onError) this.onError(err);
          reject(err);
        }
      };

      runNext();
    });
  }

  /**
   * Abort the upload
   */
  abort() {
    this.aborted = true;
  }

  setOnProgress(callback) { this.onProgress = callback; return this; }
  setOnComplete(callback) { this.onComplete = callback; return this; }
  setOnError(callback)    { this.onError = callback;    return this; }
}

/**
 * Helper — upload a file with parallel chunking and progress tracking.
 * Drop-in replacement for the old uploadFileInChunks.
 */
export const uploadFileInChunks = async (file, fileType, callbacks = {}) => {
  const uploader = new ChunkUploader(file, fileType);

  if (callbacks.onProgress) uploader.setOnProgress(callbacks.onProgress);
  if (callbacks.onComplete) uploader.setOnComplete(callbacks.onComplete);
  if (callbacks.onError)    uploader.setOnError(callbacks.onError);

  return await uploader.uploadParallel(DEFAULT_CONCURRENCY);
};

/**
 * Run `worker` over `items` with at most `concurrency` running at once.
 *
 * Generic concurrency-capped batcher for independent per-item pipelines —
 * e.g. one (video, feed) pair's "upload file(s) then submit" sequence, or
 * one platform's local file upload. Lets callers replace a `for (...) {
 * await ... }` loop (which fully serializes unrelated work) with bounded
 * parallelism instead of an unbounded `Promise.all` that could open too
 * many simultaneous chunked uploads (each item may itself already open up
 * to `DEFAULT_CONCURRENCY` concurrent chunk requests).
 */
export const runWithConcurrency = async (items, worker, concurrency = 3) => {
  if (!items || items.length === 0) return [];

  const results = new Array(items.length);
  let nextIndex = 0;
  const limit = Math.max(1, Math.min(concurrency, items.length));

  const runners = Array.from({ length: limit }, async () => {
    while (nextIndex < items.length) {
      const current = nextIndex++;
      results[current] = await worker(items[current], current);
    }
  });

  await Promise.all(runners);
  return results;
};

export default ChunkUploader;
